import {useCallback} from 'react';
import {AssetRecordType, createShapeId, Editor, fitFrameToContent, JsonArray, JsonObject, OptionalKeys} from "@tldraw/tldraw";
import {TLAssetId, TLFrameShape, TLRecord, TLShape, TLShapeId, TLShapePartial, TLUnknownShape} from "@tldraw/tlschema";
import {
    CommandType,
    FetchImagesSubTaskProcess,
    FetchImagesTaskProcess,
    GenerateTagsTaskProcess,
    taskProcess,
    TaskStatusType
} from "../types/taskFlow.types";
import {getLocalConversionId, getLocalStorage} from "../util/storage";
import {useSelector} from "react-redux";
import {RootState, store} from "../store";
import {setCurrentDialogImageInfo} from "../store/taskSlice";
import {setCurrentStepId} from "../store/drawSlice";

import {useTranslation} from 'react-i18next'

interface TaskParams {
    toolName: string;
    parameters: {
        inputText: string;
        [key: string]: any;
    };
}

// 笔记样式类型
export type NoteStyle = 'default' | 'warning' | 'info' | 'success';

export const useCanvasControl = (editor: Editor | undefined) => {

    const dialogImageInfo = useSelector((state: RootState) => state.task.dialogImageInfo);
    // 主容器默认宽高
    let mainFrameWidth = 1080;
    const mainFrameHeight = 160

    // 文字间隔
    const baseFontWidth = 13;

    // 子容器默认宽度
    let childFrameWidth = 500;
    // 子容器gap
    const childFrameGap = 26.6

    const temporary_url = process.env.REACT_APP_PROXY_TARGET ?? ''

    const {t} = useTranslation()

    const calculateMainWidth = (n: number) => {
        return childFrameGap * (n + 1) + childFrameWidth * n
    }

    const calculateShapeWidth = (n: number, currentMainWidth: number, index: number) => { // 动态计算Shape应该进入的位置
        const W1 = Math.max((currentMainWidth - calculateMainWidth(n)) / 2, 0)
        return W1 + childFrameGap + (childFrameWidth + childFrameGap) * (index - 1);
    }

    const runWithoutHistory = useCallback((fn: () => void) => {
        if (!editor) return;
        editor.run(fn, {history: 'ignore'});
    }, [editor]);

    const syncCanvasData = useCallback((task: taskProcess[]) => {
        if (!editor) return;
        const shapes = editor.getCurrentPageShapes();

        task.forEach(t => {
            const frameShape = shapes.find(shape => shape.meta.taskId === t.id && shape.type === 'frame');

            const taskLoadingShape = shapes.find(shape => shape.parentId === frameShape?.id && shape.type === 'task-loading-shape');

            const incomplete = (taskLoadingShape && 'props' in taskLoadingShape && 'loading' in taskLoadingShape.props)
                ? (taskLoadingShape.props as { loading: unknown }).loading
                : undefined;

            if (t.status === TaskStatusType.COMPLETED && incomplete) { // 当任务完成，但画板还未完成时，更新画板
                if (t.triggerCmd === CommandType.FETCH_IMAGES) {
                    const data = (t?.response as FetchImagesTaskProcess)?.data
                    replaceImagesFrame(t.id, data, CommandType.FETCH_IMAGES)
                }
                if (t.triggerCmd === CommandType.GENERATE_IMAGES) {
                    const data = (t?.response as FetchImagesTaskProcess)?.data
                    data && replaceImagesFrame(t.id, data, CommandType.GENERATE_IMAGES)
                }
                if ((CommandType.GENERATE_TAGS === t.triggerCmd || CommandType.GENERATE_PLANS === t.triggerCmd)) {
                    const data = (t?.response as GenerateTagsTaskProcess)?.data
                    data && replaceLabelsFrame(t.id, data)
                }
            }
        })
    }, [editor])

    const updateMainContainerStatus = useCallback((status: TaskStatusType, toolName: CommandType, taskId?: number) => {
        if (!editor) return
        const shapes = editor.getCurrentPageShapes();

        const statusLabels: Record<TaskStatusType, string> = {
            [TaskStatusType.PENDINGEXECUTION]: t('pending'),
            [TaskStatusType.CONFIRMING]: t('waiting_confirm'),
            [TaskStatusType.COMPLETED]: t('confirmed'),
            [TaskStatusType.CANCELED]: t('cancelled'),
            [TaskStatusType.EXECUTING]: t('executing'),
            [TaskStatusType.PENDING]: t('waiting'),
            [TaskStatusType.EXCEPTION]: t('task_failed'),
        };

        const mainShape = shapes.find(shape => shape.type === 'step-label-shape' && shape.meta.taskId === taskId && shape.meta.toolName === toolName);
        if (mainShape) {
            runWithoutHistory(() => {
                editor.updateShapes([
                    {
                        id: mainShape.id, // required
                        type: mainShape.type, // required
                        isLocked: (status === TaskStatusType.COMPLETED || status === TaskStatusType.CANCELED),
                        props: {
                            status: statusLabels[status]
                        }
                    },
                ])
            })
        }
    }, [editor])

    const doubleClickArouse = useCallback((shape?: TLShape) => {
        if (!editor || dialogImageInfo || !shape) return;

        const assetId = 'assetId' in shape.props ? shape.props.assetId : ''
        const asset = editor.getAsset(assetId as TLAssetId);

        if (!asset) return;

        const imageData = asset?.meta?.imageData as string
        const taskId = shape.meta.taskId as number
        const content = JSON.parse(imageData) as FetchImagesSubTaskProcess

        store.dispatch(setCurrentDialogImageInfo({
            description: content?.description ?? '',
            note: content?.note ?? '',
            groupId: content?.groupId ?? 0,
            tags: content?.tags ?? [],
            url: content?.url ? `${temporary_url}${content?.url}` : '',
            taskId
        }));

    }, [editor, dialogImageInfo])

    // 创建一个hover bar
    const createHoverBar = useCallback((shape?: TLShape) => {
        if (!editor) return;

        const shapes = editor.getCurrentPageShapes();

        const barshape = shapes.find(shape => shape.type === 'hover-tool-bar');

        if (barshape && (!shape || barshape?.meta.shapeId !== shape?.id)) {
            runWithoutHistory(() => {
                if (!barshape.isLocked) {

                    editor.deleteShape(barshape.id);
                } else {
                    editor.updateShapes([{
                        id: barshape.id,
                        type: barshape.type,
                        isLocked: false
                    }]);
                    editor.deleteShape(barshape.id);
                }
            })
        }

        if (barshape || !shape) return;

        const assetId = 'assetId' in shape.props ? shape.props.assetId : ''
        const asset = editor.getAsset(assetId as TLAssetId);

        const imageData = asset?.meta?.imageData
        const taskId = shape.meta?.taskId

        if (!taskId) return;

        runWithoutHistory(() => {
            editor.createShape({
                type: 'hover-tool-bar',
                x: shape.x - 10,
                y: shape.y - 50,
                parentId: shape?.parentId,
                isLocked: true,
                props: {
                    text: '',
                    status: '',
                    w: 165,
                    loading: true,
                    taskId: taskId,
                    content: imageData,
                },
                meta: {
                    shapeId: shape.id,
                }
            });
        })

    }, [editor])


    const createDialogHoverBar = useCallback((shape?: TLShape) => {
        if (!editor) return;

        const shapes = editor.getCurrentPageShapes();

        const barshape = shapes.find(shape => shape.type === 'dialog-tool-bar');

        if (barshape && (!shape || barshape?.meta.shapeId !== shape?.id)) {
            runWithoutHistory(() => {
                if (!barshape.isLocked) {
                    editor.deleteShape(barshape.id);
                } else {
                    editor.updateShapes([{
                        id: barshape.id,
                        type: barshape.type,
                        isLocked: false
                    }]);
                    editor.deleteShape(barshape.id);
                }
            })
        }

        if (barshape || !shape) return;

        /*        const assetId = 'assetId' in shape.props ? shape.props.assetId : ''
                const asset = editor.getAsset(assetId as TLAssetId);

                const imageData = asset?.meta?.imageData
                const taskId = shape.meta?.taskId

                if (!taskId) return;*/

        runWithoutHistory(() => {
            editor.createShape({
                type: 'dialog-tool-bar',
                x: shape.x + (shape as TLFrameShape).props.w + 20,
                y: shape.y + 100,
                parentId: shape?.parentId,
                isLocked: true,
                props: {
                    stepId: '',
                    w: 220,
                },
                meta: {
                    shapeId: shape.id,
                }
            });
        })

    }, [editor])

    const getSelectFrameShape = useCallback((parentStepId?: string) => {
        if (!editor || !parentStepId) return
        const shapes = editor.getCurrentPageShapes();

        const parentShape = shapes.find(shape => shape.type === 'frame' && shape.meta.stepId === parentStepId);

        return parentShape?.meta.hasChildren;

    }, [editor])

    const updateParentShape = useCallback((record: TLRecord[]) => {
        if (!editor) return;

        let lastParentStepId: string | number | boolean | JsonObject | JsonArray | null | undefined = undefined;

        const shapes = editor.getCurrentPageShapes();

        record.forEach((shape => {
            if (shape.meta.isMainContainer) {
                const isBranchHead = shape.meta.isBranchHead;
                const parentStepId = shape.meta.parentStepId;
                const hasChildren = shape.meta.hasChildren;
                const parentShape = shapes.find(shape => shape.type === 'frame' && shape.meta.stepId === parentStepId);
                if (parentShape && !hasChildren && !isBranchHead) {
                    runWithoutHistory(() => {
                        editor.updateShape({
                            id: parentShape!.id,
                            type: parentShape!.type,
                            meta: {
                                ...parentShape.meta,
                                hasChildren: false
                            }
                        })
                    })
                }
                lastParentStepId = parentStepId
            }
        }))

        const parentShape = shapes.find(shape => shape.type === 'frame' && shape.meta.stepId === lastParentStepId);

        const selectedShape = shapes.find(shape => shape.type === 'frame' && shape.meta.selected);

        if (selectedShape) return;

        if (!parentShape) {
            const lastMainContainer = [...shapes].reverse().find(shape => shape.meta.isMainContainer);
            lastParentStepId = lastMainContainer?.meta.stepId
        }

        if (lastParentStepId) store.dispatch(setCurrentStepId(lastParentStepId as string))

    }, [editor])

    const cleanSelectStatus = useCallback(() => {
        if (!editor) return

        const shapes = editor.getCurrentPageShapes();

        const selectShape = shapes.filter(shape => shape.meta.selected);

        const needUpdateShape = selectShape.map(shape => {
            return {
                id: shape.id, // required
                type: shape.type, // required
                meta: {
                    ...shape.meta,
                    selected: false
                }
            }
        })

        runWithoutHistory(() => {
            editor.updateShapes(needUpdateShape)
        })

    }, [editor])

    const selectShape = useCallback((shape: TLShape) => {
        if (!editor || !shape) return

        cleanSelectStatus()

        if (shape.meta.isMainContainer) {
            const stepId = shape.meta.stepId as string;
            updateLastStepId(stepId)
        }

        runWithoutHistory(() => {
            editor.updateShape({
                id: shape!.id,
                type: shape!.type,
                meta: {
                    ...shape.meta,
                    selected: true
                }
            })
        })

    }, [editor])


    const selectFrameShape = useCallback((parentStepId?: string) => {
        if (!editor || !parentStepId) return

        const shapes = editor.getCurrentPageShapes();

        const parentShape = shapes.find(shape => shape.type === 'frame' && shape.meta.stepId === parentStepId);

        const selectShape = shapes.filter(shape => shape.meta.selected);

        if (parentShape?.meta.selected) return;

        const needUpdateShape = selectShape.map(shape => {
            return {
                id: shape.id, // required
                type: shape.type, // required
                meta: {
                    ...shape.meta,
                    selected: false
                }
            }
        })

        if (parentShape) {
            runWithoutHistory(() => {
                editor.updateShape({
                    id: parentShape!.id,
                    type: parentShape!.type,
                    meta: {
                        ...parentShape.meta,
                        selected: true
                    }
                })
                editor.updateShapes(needUpdateShape)
            })
        }

    }, [editor])


    const stepsContainer = useCallback((stepId: string, toolName: CommandType, parentStepId?: string, extra?: string) => {
        if (!editor) return;

        const shapes = editor.getCurrentPageShapes();

        const shape = shapes.find(shape => shape.type === 'frame' && shape.meta.stepId === stepId);

        const parentShape = shapes.find(shape => shape.type === 'frame' && shape.meta.stepId === parentStepId);

        if (shape) return;

        cleanSelectStatus()

        let isCard = false;

        if (extra) {
            const content = JSON.parse(extra);
            isCard = content.chatOn.type === 'image' || content.chatOn.type === 'card'
        }

        let lowestY = 0;
        let leftX = Infinity;  // 初始化为最大值
        let rightX = -Infinity;  // 初始化为最小值

        // 找到parentStepId的中心点
        if (parentShape) {
            const frameShape = parentShape as TLFrameShape;
            if (parentShape.meta.hasChildren || isCard) {
                lowestY = frameShape.y - 25;

                shapes.forEach((shape) => {
                    if (shape.type === 'frame' && shape.meta.toolName) {
                        const frameShape = shape as TLFrameShape;
                        console.log(frameShape, t('最右节点'))
                        if ((frameShape.x + frameShape.props.w) > rightX) {
                            leftX = frameShape.x + frameShape.props.w;
                            rightX = leftX + mainFrameWidth
                        }
                    }
                });

                leftX += 1500;
                rightX += 1500;
            } else {
                // 更新最低点
                lowestY = Math.max(lowestY, frameShape.y + frameShape.props.h);
                // 更新最左和最右的 x 坐标
                rightX = frameShape.x + frameShape.props.w;
                leftX = rightX - frameShape.props.w;
            }
        }

        // 找到画布中所有形状的最低点, 暂时注释掉
        /*        shapes.forEach((shape) => {
                    if (shape.type === 'frame' && shape.meta.toolName) {
                        const frameShape = shape as TLFrameShape;
                        // 更新最低点
                        lowestY = Math.max(lowestY, frameShape.y + frameShape.props.h);
                        // 更新最左和最右的 x 坐标
                        leftX = Math.min(leftX, frameShape.x);
                        rightX = Math.max(rightX, frameShape.x + frameShape.props.w);
                    }
                });*/

        // 如果没有找到任何 frame，使用默认值
        if (leftX === Infinity) leftX = 0;
        if (rightX === -Infinity) rightX = 0;

        const center = editor.getViewportPageBounds().center;
        if (!center) return;

        if (toolName === CommandType.GENERATE_IMAGES) {
            childFrameWidth = 750
            mainFrameWidth = 1580
        }

        // 计算中心点：最左和最右 frame 的中心
        const centerX = (leftX + rightX) / 2;

        let startY = centerX ? lowestY + 50 : center.y - mainFrameHeight / 2;
        let startX = centerX ? centerX - mainFrameWidth / 2 : center.x - mainFrameWidth / 2; // 将新 frame 的中心对齐到计算出的中心点

        // 创建主frame
        const newShapeId = createShapeId();

        editor?.centerOnPoint(
            {x: startX + (mainFrameWidth / 2), y: startY},
            {animation: {duration: 500, easing: (t) => t * t}}
        )

        const iframeShape = {
            id: newShapeId,
            type: 'frame',
            x: startX, // 让frame水平居中
            y: startY, // 让frame垂直居中
            props: {
                w: mainFrameWidth,
                h: mainFrameHeight,
                name: stepId,
            },
            meta: {
                stepId,
                toolName,
                isMainContainer: true,
                hasChildren: false,
                parentStepId,
                isBranchHead: !!parentShape?.meta.hasChildren,
                selected: true
            }
        }

        runWithoutHistory(() => {
            editor.createShape(iframeShape);
        })

        if (parentStepId && !isCard) { // 修改父节点的子集状态
            if (parentShape) {
                runWithoutHistory(() => {
                    editor.updateShape({
                        id: parentShape!.id,
                        type: parentShape!.type,
                        meta: {
                            ...parentShape.meta,
                            selected: false,
                            hasChildren: true
                        }
                    })
                })
            }
        }

        updateLastStepId(stepId)

        store.dispatch(setCurrentStepId(stepId))

        return newShapeId;
    }, [editor]);


    // 创建主容器
    const stepsProcess = useCallback((prompt: string, index: number, status: TaskStatusType, toolName: CommandType, taskId?: number, tasks?: taskProcess[]) => {
        if (!editor) return;

        // 找到画布中所有形状的最低点
        const shapes = editor.getCurrentPageShapes();

        const excuseEnd = tasks?.filter(t => {
            const frameShape = shapes.find(shape => shape.meta.taskId === t.id && shape.type === 'frame');

            const taskLoadingShape = shapes.find(shape => shape.parentId === frameShape?.id && shape.type === 'task-loading-shape');

            const incomplete = (taskLoadingShape && 'props' in taskLoadingShape && 'loading' in taskLoadingShape.props)
                ? (taskLoadingShape.props as { loading: unknown }).loading
                : undefined;

            return (t.status === TaskStatusType.COMPLETED && incomplete);
        })


        if (excuseEnd && excuseEnd.length) return;

        const shape = shapes.find(shape => shape.type === 'frame' && shape.meta.taskId === taskId && shape.meta.toolName === toolName);

        if (shape) return;

        let lowestY = 0;
        let leftX = Infinity;  // 初始化为最大值
        let rightX = -Infinity;  // 初始化为最小值

        shapes.forEach((shape) => {
            if (shape.type === 'frame' && shape.meta.toolName) {
                const frameShape = shape as TLFrameShape;
                // 更新最低点
                lowestY = Math.max(lowestY, frameShape.y + frameShape.props.h);
                // 更新最左和最右的 x 坐标
                leftX = Math.min(leftX, frameShape.x);
                rightX = Math.max(rightX, frameShape.x + frameShape.props.w);
            }
        });

        // 如果没有找到任何 frame，使用默认值
        if (leftX === Infinity) leftX = 0;
        if (rightX === -Infinity) rightX = 0;

        const center = editor.getViewportPageBounds().center;
        if (!center) return;

        if (toolName === CommandType.GENERATE_IMAGES) {
            childFrameWidth = 750
            mainFrameWidth = 1580
        }

        // 计算中心点：最左和最右 frame 的中心
        const centerX = (leftX + rightX) / 2;

        let startY = centerX ? lowestY + 50 : center.y - mainFrameHeight / 2;
        let startX = centerX ? centerX - mainFrameWidth / 2 : center.x - mainFrameWidth / 2; // 将新 frame 的中心对齐到计算出的中心点

        // 创建主frame
        const newShapeId = createShapeId();

        editor?.centerOnPoint(
            {x: startX + (mainFrameWidth / 2), y: startY},
            {animation: {duration: 500, easing: (t) => t * t}}
        )

        const iframeShape = {
            id: newShapeId,
            type: 'frame',
            x: startX, // 让frame水平居中
            y: startY, // 让frame垂直居中
            props: {
                w: mainFrameWidth,
                h: mainFrameHeight,
                name: 'step' + index,
            },
            meta: {
                taskId,
                toolName,
                isMainContainer: true
            }
        }

        runWithoutHistory(() => {
            editor.createShape(iframeShape);
        })

        const computerWidth = () => {
            const promptLength = prompt.length;
            return Math.min(promptLength * baseFontWidth + 140, mainFrameWidth - 20);
        }

        // 状态标签映射
        const statusLabels: Record<TaskStatusType, string> = {
            [TaskStatusType.PENDINGEXECUTION]: t('pending'),
            [TaskStatusType.CONFIRMING]: t('waiting_confirm'),
            [TaskStatusType.COMPLETED]: t('confirmed'),
            [TaskStatusType.CANCELED]: t('cancelled'),
            [TaskStatusType.EXECUTING]: t('executing'),
            [TaskStatusType.PENDING]: t('waiting'),
            [TaskStatusType.EXCEPTION]: t('task_failed'),
        };

        // 创建step-label-shape，使其在frame内水平居中
        runWithoutHistory(() => {
            editor.createShape({
                type: 'step-label-shape',
                x: startX + mainFrameWidth / 2 - computerWidth() / 2,
                y: startY + 20, // 让frame垂直居中
                props: {
                    index,
                    text: prompt || t('excuse_step'),
                    status: statusLabels[status],
                    w: computerWidth(),
                },
                meta: {
                    taskId,
                    toolName
                }
            });
        })

        return newShapeId;
    }, [editor]);

    const heightMeasurement = useCallback((content: string, padding: number) => {
        // 创建虚拟DOM测量文本高度
        const measureDiv = document.createElement('div');
        let contentHeight = 0;
        measureDiv.style.position = 'absolute';
        measureDiv.style.visibility = 'hidden';
        measureDiv.style.whiteSpace = 'pre-wrap';
        measureDiv.style.wordWrap = 'break-word';
        measureDiv.style.width = '500px'; // 与shape宽度一致
        measureDiv.style.font = '12px sans-serif'; // 与实际样式一致
        measureDiv.style.boxSizing = 'border-box';
        measureDiv.style.lineHeight = '25px'; // 行高
        measureDiv.style.padding = padding + 'px'; // 内边距
        measureDiv.style.fontFamily = 'monospace';
        measureDiv.textContent = content;

        document.body.appendChild(measureDiv);
        contentHeight = measureDiv.offsetHeight;
        document.body.removeChild(measureDiv);
        return contentHeight
    }, [editor])

    const setLabelToFrameNotDependent = useCallback((toolName: CommandType, taskId: number, text: string) => {
        if (!editor) return;

        const shapes = editor.getCurrentPageShapes();
        const center = editor.getViewportPageBounds().center;

        const existTaskId = shapes.find(shape => shape.meta.taskId === taskId);

        if (existTaskId) return;

        let lowestY = 0;
        let leftX = Infinity;  // 初始化为最大值
        let rightX = -Infinity;  // 初始化为最小值

        shapes.forEach((shape) => {
            if (shape) {
                const frameShape = shape as TLFrameShape;
                const parentShape = editor.getShape(frameShape.parentId);
                if (parentShape?.meta?.taskId) return;
                if (frameShape.y && frameShape.props.h) {
                    lowestY = Math.max(lowestY, frameShape.y + frameShape.props.h);
                }
                if (frameShape.x) {
                    leftX = Math.min(leftX, frameShape.x);
                }
                if (frameShape.x && frameShape.props.w) {
                    rightX = Math.max(rightX, frameShape.x + frameShape.props.w);
                }
            }
        });


        // 如果没有找到任何 frame，使用默认值
        if (leftX === Infinity) leftX = 0;
        if (rightX === -Infinity) rightX = 0;

        const centerX = (leftX + rightX) / 2;

        let startY = centerX ? lowestY + 50 : center.y - 150 / 2;
        let startX = centerX ? centerX - 500 / 2 : center.x - 500 / 2;

        editor?.centerOnPoint(
            {x: startX + 250, y: startY},
            {animation: {duration: 500, easing: (t) => t * t}}
        )

        const newShapeId = createShapeId();

        const iframeShape = {
            id: newShapeId,
            type: 'frame',
            x: startX,
            y: startY,
            props: {
                w: childFrameWidth,
                h: 144,
                name: text,
            },
            meta: {
                taskId: taskId,
                toolName,
            }
        }

        runWithoutHistory(() => {
            editor.createShape(iframeShape);
        })

        const computerWidth = () => {
            const promptLength = text.length;
            const width = promptLength * baseFontWidth + 100;
            return childFrameWidth; //width > 460 ? 460 : width;
        }

        runWithoutHistory(() => {
            editor.createShape({
                type: 'task-loading-shape',
                x: startX + childFrameWidth / 2 - computerWidth() / 2,
                y: startY,
                props: {
                    text: text,
                    status: t('waiting_confirm'),
                    w: computerWidth(),
                    loading: true,
                    taskId: taskId,
                    showLeve2: false
                },
                meta: {
                    parentId: taskId
                }
            });
        })

        runWithoutHistory(() => {
            editor.createShape({
                type: 'loading-shape',
                x: startX + childFrameWidth / 2 - computerWidth() / 2,
                y: startY + 75,
                props: {
                    text: text,
                    status: t('waiting_confirm'),
                    w: computerWidth(),
                    loading: true,
                    taskId: taskId,
                },
                meta: {
                    parentId: taskId
                }
            });
        })
    }, [editor])

    const replaceDialogFrame = useCallback((taskId: number, textInput: string, images: string[]) => {
        if (!editor) return
        const shapes = editor.getCurrentPageShapes();
        const relatedTaskShapes = shapes.find(shape => shape.meta.taskId === taskId);
        const parentShape = shapes.find(shape => shape.id === relatedTaskShapes?.parentId);

        const removeShape = shapes.find(shape => shape.meta.parentId === taskId && shape.type === 'loading-shape');
        const removeShape1 = shapes.find(shape => shape.meta.parentId === taskId && shape.type === 'task-loading-shape');

        if (removeShape) {
            runWithoutHistory(() => {
                editor.updateShape({
                    id: removeShape!.id,
                    type: removeShape!.type,
                    isLocked: true,
                    props: {
                        text: '',
                        loading: false,
                    }
                })
            })
        } else {
            console.log(removeShape, taskId)
        }

        const contentHeight = heightMeasurement(textInput, 20)

        runWithoutHistory(() => {
            const relatedShapeHeight = (340 + 10) * images.length + contentHeight + 6
            editor.createShape({
                type: 'dialog-shape',
                x: 0,
                y: 0,
                isLocked: true,
                parentId: relatedTaskShapes?.id,
                props: {
                    text: textInput,
                    status: t('waiting_confirm'),
                    w: 500,
                    imageData: images,
                    h: relatedShapeHeight,
                    cover: true
                }
            })

            images.forEach((img, index)=>{
                const assetId = AssetRecordType.createId();

                const imgInstance = new Image();
                imgInstance.src = temporary_url + img;
                imgInstance.onload = function() {
                    const width = imgInstance.width;
                    const height = imgInstance.height;

                    const splitImgGetUUid = img.split('/').pop();

                    const imageData = {
                        lod0: img,
                        lod1: img,
                        note: "YIdDTlBjuMZlKjgSnmiD",
                        path: img,
                        description: null,
                        tags: null,
                        width: width,
                        height: height,
                        uuid: splitImgGetUUid,
                        url: img,
                        taskId: taskId
                    }

                    const aspectRatio = 320 / height;
                    const imageWidth = Math.min(width * aspectRatio, 460);

                    // 创建资源
                    editor.createAssets([{
                        id: assetId,
                        type: 'image',
                        typeName: 'asset',
                        props: {
                            name: img,
                            src: temporary_url + img,
                            w: imageWidth,
                            h: 320,
                            mimeType: 'image/png',
                            isAnimated: false
                        },
                        meta: {
                            originUrl: img,
                            uuid: splitImgGetUUid,
                            imageData: JSON.stringify(imageData),
                        }
                    }])


                    editor.createShape({
                        type: 'image',
                        x: 480 - imageWidth,
                        y: (index * 350) + contentHeight + 10,
                        props: {
                            assetId: assetId,
                            w: imageWidth,
                            h: 320,
                        },
                        parentId: relatedTaskShapes?.id,
                        meta: {
                            taskId
                        }
                    });
                };

                })

            editor.updateShape({
                id: relatedTaskShapes!.id,
                type: relatedTaskShapes!.type,
                props: {
                    h: relatedShapeHeight
                }
            })
            if (parentShape) {
                editor.updateShapes([
                    {
                        id: parentShape.id, // required
                        type: parentShape.type, // required
                        props: {
                            // @ts-ignore
                            h: parentShape.props.h + relatedShapeHeight - relatedTaskShapes?.props.h,
                        },
                    },
                ])
            }
        })

    }, [editor])

    const redrawYAxis = useCallback((taskId: number) => {
        if (!editor) return;
        const shapes = editor.getCurrentPageShapes();

        const relatedTaskShapes = shapes.find(shape => shape.meta.taskId === taskId);
        const parentShape = shapes.find(shape => shape.id === relatedTaskShapes?.parentId) as TLFrameShape;

        if (!parentShape || !relatedTaskShapes) {
            return;
        }

        const childrenShapes = shapes.filter(react => react.parentId === parentShape.id && react.type !== 'step-label-shape');

        // 按Y轴分组并排序
        const yAxisGroups = new Map<number, TLShape[]>();
        childrenShapes.forEach(shape => {
            const y = Math.floor(shape.y);
            if (!yAxisGroups.has(y)) {
                yAxisGroups.set(y, []);
            }
            yAxisGroups.get(y)?.push(shape);
        });

        // 获取每组中最高的shape
        const sortedGroups = Array.from(yAxisGroups.entries())
            .sort(([a], [b]) => a - b)
            .map(([y, shapes]) => {
                const maxHeight = Math.max(...shapes.map(s => 'props' in s ? (s.props as any).h || 0 : 0));
                return {y, shapes, maxHeight};
            });

        // 重新计算位置并保持20间距
        let prevBottom = 0;
        const updates: TLShapePartial[] = [];

        sortedGroups.forEach((group, index) => {
            if (index === 0) {
                // 第一组保持原位置
                prevBottom = group.y + group.maxHeight;
            } else {
                // 计算新Y位置
                const newY = prevBottom + 40;
                const yOffset = newY - group.y;

                // 更新该组所有shape的Y坐标
                group.shapes.forEach(shape => {
                    updates.push({
                        id: shape.id,
                        type: shape.type,
                        y: shape.y + yOffset
                    });
                });

                prevBottom = newY + group.maxHeight;
            }
        });

        // 批量更新
        if (updates.length > 0) {
            runWithoutHistory(() => {

                const newParentHeight = prevBottom + 40;

                editor.updateShapes([
                    {
                        id: parentShape.id,
                        type: parentShape.type,
                        props: {
                            h: newParentHeight
                        }
                    },
                    ...updates
                ]);

            });
        }
    }, [editor]);


    const replaceDescribeFrame = useCallback((taskId?: number, content?: any, toolName?: CommandType, errorMsg?: string) => {
        if (!editor) return
        const shapes = editor.getCurrentPageShapes();
        const relatedTaskShapes = shapes.find(shape => shape.meta.taskId === taskId);
        const parentShape = shapes.find(shape => shape.id === relatedTaskShapes?.parentId) as TLFrameShape;

        const removeShape = shapes.find(shape => shape.meta.parentId === taskId && shape.type === 'loading-shape');
        const removeShape1 = shapes.find(shape => shape.meta.parentId === taskId && shape.type === 'task-loading-shape');

        let emptyText = ''
        if (errorMsg) emptyText = errorMsg

        if (removeShape) {
            runWithoutHistory(() => {
                editor.updateShape({
                    id: removeShape!.id,
                    type: removeShape!.type,
                    isLocked: true,
                    props: {
                        text: emptyText,
                        loading: false,
                    }
                })
            })
        } else {
            console.log(removeShape, taskId)
        }


        if (emptyText) return;

        if (!relatedTaskShapes || !parentShape) return;

        const contentHeight = heightMeasurement(content, 10)

        runWithoutHistory(() => {
            editor.createShape({
                type: 'describe-shape',
                x: 0,
                y: 0,
                isLocked: true,
                parentId: relatedTaskShapes?.id,
                props: {
                    text: content,
                    w: 500,
                    h: contentHeight
                }
            })

            editor.updateShape({
                id: relatedTaskShapes!.id,
                type: relatedTaskShapes!.type,
                props: {
                    h: contentHeight
                }
            })
        })

    }, [editor])


    const setLabelToFrame = useCallback((toolName: CommandType, taskId: number, text: string, stepId: string, direction: {
        direction: 'vertical' | 'horizontal',
        index: number,
        batchCount: number,
        batchId: string
    }) => {
        if (!editor) return;
        const shapes = editor.getCurrentPageShapes();

        let shape = shapes.find(shape => shape.type === 'frame' && shape.meta.stepId === stepId) as TLFrameShape;

        const existCard = shapes.find(shape => shape.meta.taskId === taskId);

        if (!shape || existCard) return;

        let lastX: number;
        let lastY: number;
        let containerHeight: number

        let mainContainWidth = 0;
        let moveTitleX = 0;

        let mainContainerWidth = shape.props.w;
        const mainContainerHeight = shape.props.h;

        const childrenLength = shapes.filter(react => react.parentId === shape.id && react.type !== 'step-label-shape');

        // 先更新容器宽度
        const miniMainContainerWidth = calculateMainWidth(direction.batchCount);

        if (miniMainContainerWidth > mainContainerWidth) {
            const moveUpShapes = childrenLength.map(childShape => {
                const offsetX = calculateShapeWidth(
                    childShape.meta.batchCount as number,
                    miniMainContainerWidth,
                    childShape.meta.index as number + 1
                ) // 获取在容器内的x坐标
                return {
                    id: childShape.id,
                    type: childShape.type,
                    x: offsetX
                }
            })
            // 更新主容器宽度 & 上部分节点居中
            runWithoutHistory(() => {
                moveTitleX = (miniMainContainerWidth - mainContainerWidth) / 2 // 向左移一半
                editor.updateShapes([
                    {
                        id: shape.id, // required
                        type: shape.type, // required
                        x: shape.x - moveTitleX,
                        props: {
                            w: miniMainContainerWidth,
                        },
                    },
                    ...moveUpShapes,
                ])
            })
            shape = editor.getShape(shape.id) as TLFrameShape // 获取最新的主容器
            mainContainerWidth = shape.props.w;
        }

        lastX = shape.x + calculateShapeWidth(direction.batchCount, mainContainerWidth, direction.index + 1) // 获取在容器内的x坐标

        let lowestY;
        if (direction.direction === 'vertical' || direction.index === 0) {
            let lowestY;
            if (childrenLength.length > 0) {
                // @ts-ignore
                lowestY = Math.max(...childrenLength.map(child => child.y + child.props.h));
            } else {
                lowestY = 0;
            }
            lastY = shape.y + lowestY + 30;
            containerHeight = mainContainerHeight + 30 + (childrenLength.length > 1 ? 144 : 80);
        } else {
            lowestY = childrenLength.find(cxt => cxt.meta.batchId === direction.batchId)?.y || 0
            lastY = shape.y + lowestY;
            containerHeight = mainContainerHeight
        }

        runWithoutHistory(() => { // 更新主容器高度
            editor.updateShapes([
                {
                    id: shape.id, // required
                    type: shape.type, // required
                    props: {
                        h: containerHeight
                    },
                },
            ])
        })

        if (typeof lastX !== 'number' || isNaN(lastX)) {
            console.log(lastX, direction.batchCount, mainContainerWidth, '错误信息');
            return;
        }

        const newShapeId = createShapeId();

        let batchCreateShapes: OptionalKeys<{ id: TLShapeId; type: string; props?: object | undefined; meta?: Partial<JsonObject> | undefined; } & Partial<Omit<TLUnknownShape, "id" | "type" | "props" | "meta">>, "id">[] | { id?: TLShapeId; type: string; x: number; y: number; props: { w: number; h: number; name: string; } | { text: string; status: any; w: number; loading: boolean; taskId: number; showLeve2: boolean; } | { text: string; status: any; w: number; loading: boolean; taskId: number;timeEstimate: number, startTime: number }; meta: { taskId: number; commandType: CommandType; mainContainWidth: number; batchId: string; batchCount: number; index: number; } | { parentId: number; } | { parentId: number; }; parentId?: TLShapeId; }[] = []

        const iframeShape = {
            id: newShapeId,
            type: 'frame',
            x: lastX,
            y: lastY,
            props: {
                w: childFrameWidth,
                h: 144,
                name: text,
            },
            meta:{
                taskId: taskId,
                commandType: toolName,
                mainContainWidth: mainContainerWidth,
                batchId: direction.batchId || '',
                batchCount: direction.batchCount,
                index: direction.index
            }
        }

        batchCreateShapes.push(iframeShape)

        const computerWidth = () => {
            const promptLength = text.length;
            const width = promptLength * baseFontWidth + 100;
            return childFrameWidth; //width > 460 ? 460 : width;
        }

        const notShowLabel = ![CommandType.CHAT, CommandType.PARSER_TASK_CARD].includes(toolName);

        const estimatedTime: Partial<Record<CommandType, number>> = {
            [CommandType.FETCH_IMAGES]: 30,
            [CommandType.GENERATE_IMAGES]: 50,
            [CommandType.EDIT_IMAGES]: 50,
        };

        const timeEstimate = estimatedTime[toolName] ?? 0;
        const startTime = Math.floor(Date.now() / 1000);

        if (notShowLabel) {
            batchCreateShapes.push({
                type: 'task-loading-shape',
                x: childFrameWidth / 2 - computerWidth() / 2,
                y: 0,
                props: {
                    text: text,
                    status: t('waiting_confirm'),
                    w: computerWidth(),
                    loading: true,
                    taskId: taskId,
                    showLeve2: false
                },
                parentId: newShapeId,
                meta: {
                    parentId: taskId
                }
            })
        }

        batchCreateShapes.push({
            type: 'loading-shape',
            x: childFrameWidth / 2 - computerWidth() / 2,
            y: notShowLabel ?  50 :  35,
            props: {
                text: text,
                status: t('waiting_confirm'),
                w: computerWidth(),
                loading: true,
                taskId: taskId,
                timeEstimate,
                startTime
            },
            parentId: newShapeId,
            meta: {
                parentId: taskId
            }
        })

        runWithoutHistory(()=>{
            editor.createShapes(batchCreateShapes);
           // fitFrameToContent(editor, shape.id)
        })

    }, [editor])

    const fitFrameContainer = useCallback((stepId: string)=>{
        if (!editor) return;

        const shapes = editor.getCurrentPageShapes();

        const shape = shapes.find(shape => shape.type === 'frame' && shape.meta.stepId === stepId) as TLFrameShape;

        if (!shape) return;

        fitFrameToContent(editor, shape.id)
    }, [editor])

    const replaceLabelsFrame = useCallback((taskId?: number, data?:string[], errorMsg?: string) => {
        if (!editor) return;
        const shapes = editor.getCurrentPageShapes();
        const relatedTaskShapes = shapes.find(shape => shape.meta.taskId === taskId);
        const parentShape = shapes.find(shape => shape.id === relatedTaskShapes?.parentId);

        if (!parentShape) return; // 找不到主容器，需要有报错提示

        const removeShape = shapes.find(shape => shape.meta.parentId === taskId && shape.type === 'loading-shape');
        const removeShape1 = shapes.find(shape => shape.meta.parentId === taskId && shape.type === 'task-loading-shape');
        // editor.deleteShapes(removeShape)

        let emptyText = ''
        if (!data?.length) emptyText = t('no_tags_found')
        if (errorMsg) emptyText = errorMsg

        if (removeShape && removeShape1) {
            runWithoutHistory(()=>{
                editor.updateShape({
                    id: removeShape!.id,
                    type: removeShape!.type,
                    isLocked: true,
                    props: {
                        text: emptyText,
                        loading: false,
                    }
                })
                editor.updateShape({
                    id: removeShape1!.id,
                    type: removeShape1!.type,
                    isLocked: true,
                    props: {
                        loading: false
                    }
                })
            })
        }else {
            console.log(removeShape, removeShape1, taskId)
        }


        let rowLength = 15;
        let column = 12;
        let addRowCount = 0;


        data?.forEach(item => {
            const computerWidth = () => {
                const promptLength = item.length;
                return promptLength * baseFontWidth + 30;
            }

            const shapeX = computerWidth() + 10;

            if ((500 - rowLength) < shapeX) {
                rowLength = 10;
                addRowCount += 1;
                column += 45;
            }

            const startX = relatedTaskShapes!.x + parentShape!.x + rowLength;
            // const startY = relatedTaskShapes!.y + parentShape!.y + column + 80; // label 上移了，不需要额外的80高度
            const startY = relatedTaskShapes!.y + parentShape!.y + column + 40;

            runWithoutHistory(()=>{
                editor.createShape({
                    type: 'label-shape',
                    x: startX,
                    y: startY,
                    props: {
                        index: 1,
                        text: item,
                        status: t('waiting_confirm'),
                        w: computerWidth(),
                    }
                })})


            const frameHeight = (relatedTaskShapes?.props as TLFrameShape['props']).h +  (addRowCount > 1 ? column - 50 : 0)  - 10

            runWithoutHistory(()=>{
                editor.updateShape({
                    id: relatedTaskShapes!.id,
                    type: relatedTaskShapes!.type,
                    props: {
                        ...relatedTaskShapes!.props,
                        h: frameHeight
                    }
                });
            })

            const parentShapeLowY = parentShape!.y + (parentShape?.props as TLFrameShape['props']).h;
            const frameShapeLowY = parentShape!.y + relatedTaskShapes!.y + frameHeight

            if ((parentShapeLowY - frameShapeLowY) < 13) {
                runWithoutHistory(()=>{
                    editor.updateShape({
                        id: parentShape!.id,
                        type: parentShape!.type,
                        props: {
                            ...parentShape!.props,
                            h: (parentShape?.props as TLFrameShape['props']).h + 13 - (parentShapeLowY - frameShapeLowY)
                        }
                    });
                })
            }

            rowLength += shapeX;
        })

    }, [editor])

    const updateLastStepId = useCallback((stepId: string) => {
        if (!editor) return;


        const currentPageId = editor.getCurrentPageId();
        const page = editor.getPage(currentPageId);

        if (!page) return;

        const currentMeta = page.meta ?? {};

        runWithoutHistory(() => {
            editor.updatePage({
                id: currentPageId,
                meta: {
                    ...currentMeta,
                    lastStep: stepId,
                },
            });
        })

    }, [editor]);


    const getLastStepId = useCallback(() => {
        if (!editor) return;


        const currentPageId = editor.getCurrentPageId();
        const page = editor.getPage(currentPageId);

        if (!page) return;

        const currentMeta = page.meta ?? {};

        return currentMeta.lastStep;
    }, [editor]);


    const replaceImagesFrame = useCallback((taskId?: number, data?: FetchImagesSubTaskProcess[], toolName?: CommandType, errorMsg?: string) => {
        if (!editor || !data) return;
        const shapes = editor.getCurrentPageShapes();
        const relatedTaskShapes = shapes.find(shape => shape.meta.taskId === taskId);

        const parentShape = shapes.find(shape => shape.id === relatedTaskShapes?.parentId);

        // 删除现有的图片
        const removeShape = shapes.find(shape => shape.meta.parentId === taskId && shape.type === 'loading-shape');
        const removeShape1 = shapes.find(shape => shape.meta.parentId === taskId && shape.type === 'task-loading-shape');
        // editor.deleteShapes(removeShape);

        if (removeShape) {
            const loading = 'loading' in removeShape.props ? removeShape.props.loading : false;
            if (!loading) return;
        }

        let emptyText = ''

        const hasCenterImg = data.filter(img => img.isCenter);

       // if (toolName === CommandType.FETCH_IMAGES) {
            if (!hasCenterImg.length) emptyText = 'no_center_image'
            if (!data.length) emptyText = 'no_related_images'
       // }

        if (errorMsg) emptyText = errorMsg

        if (removeShape1 && removeShape){
            runWithoutHistory(()=>{
                editor.updateShape({
                    id: removeShape!.id,
                    type: removeShape!.type,
                    isLocked: true,
                    props: {
                        text: emptyText,
                        loading: false,
                    }
                })
                editor.updateShape({
                    id: removeShape1!.id,
                    type: removeShape1!.type,
                    isLocked: true,
                    props: {
                        loading: false,
                        showLeve2: !emptyText
                    }
                })
            })
        }else {
            console.log(removeShape1, removeShape, 'removeShape', taskId)
        }

        if (emptyText) return;

        // 初始化布局参数
        const COLUMN_COUNT = toolName === CommandType.FETCH_IMAGES ? 3 : 1;
        const COLUMN_GAP = 16; // 减小列间距
        const ITEM_GAP = 16; // 减小项目间距
        const FRAME_PADDING = 16; // 添加frame内边距

        // 计算实际可用宽度（考虑内边距）
       // const availableWidth = (toolName === CommandType.GENERATE_IMAGES ? 750 : 500) - (FRAME_PADDING * 2);
        const availableWidth = childFrameWidth - (FRAME_PADDING * 2); // 先统一宽度
        // 计算单列宽度
        const COLUMN_WIDTH = (availableWidth - COLUMN_GAP * (COLUMN_COUNT - 1)) / COLUMN_COUNT;

        // 初始化列高度（考虑上内边距）
        const columnHeights = new Array(COLUMN_COUNT).fill(FRAME_PADDING);

        // 遍历图片数据并创建资源
        data.forEach((imageData) => {

            if (toolName === CommandType.FETCH_IMAGES && !imageData.isCenter) return;

            if (!imageData.height || !imageData.width) return;

            const assetId = AssetRecordType.createId();

            // 计算图片高度，保持宽高比
            const aspectRatio = imageData.height / imageData.width;
            const imageHeight = COLUMN_WIDTH * aspectRatio;

            // 创建资源
            editor.createAssets([{
                id: assetId,
                type: 'image',
                typeName: 'asset',
                props: {
                    name: imageData.url,
                    src: temporary_url+imageData.lod1,
                    w: COLUMN_WIDTH,
                    h: imageHeight,
                    mimeType: 'image/png',
                    isAnimated: false
                },
                meta: {
                    originUrl: temporary_url+imageData.url,
                    uuid: imageData.uuid,
                    imageData: JSON.stringify(imageData),
                }
            }]);

            // 找出高度最小的列
            const minHeightColumn = columnHeights.indexOf(Math.min(...columnHeights));

            // 计算图片位置（考虑内边距）
            const x = FRAME_PADDING + minHeightColumn * (COLUMN_WIDTH + COLUMN_GAP);
            const y = columnHeights[minHeightColumn] + 40;

            // 创建图片shape
            runWithoutHistory(()=>{
                editor.createShape({
                    type: 'image',
                    x: x,
                    y: y,
                    props: {
                        assetId: assetId,
                        w: COLUMN_WIDTH,
                        h: imageHeight,
                    },
                    parentId: relatedTaskShapes?.id,
                    meta: {
                        taskId
                    }
                });
            })

            // 更新列高度
            columnHeights[minHeightColumn] += imageHeight + ITEM_GAP;
        });

        // 获取最大列高（考虑底部内边距）
        const maxColumnHeight = Math.max(...columnHeights) + FRAME_PADDING + 20;

        // 更新内部frame的高度
        if (maxColumnHeight > 0 && relatedTaskShapes) {
            runWithoutHistory(()=>{
                editor.updateShape({
                    id: relatedTaskShapes.id,
                    type: relatedTaskShapes.type,
                    props: {
                        ...relatedTaskShapes.props,
                        h: maxColumnHeight
                    }
                });
            })
        }

        // 更新外层主容器（parentShape）的高度
        if (maxColumnHeight > 0 && parentShape) {
            // 计算子容器相对于父容器的偏移量
            const offsetY = relatedTaskShapes!.y;
            // 计算父容器所需的总高度（子容器偏移量 + 子容器高度 + 底部间距）
            const totalHeight = offsetY + maxColumnHeight + FRAME_PADDING;

            // 只有当计算出的新高度大于现有高度时才更新
            if (totalHeight > (parentShape.props as TLFrameShape['props']).h) {
                runWithoutHistory(()=>{
                    editor.updateShape({
                        id: parentShape.id,
                        type: parentShape.type,
                        props: {
                            ...parentShape.props,
                            h: totalHeight
                        }
                    });
                })
            }
        }
    }, [editor]);


    const createCustomShape = useCallback((toolName: CommandType, taskId: number, note: string, reactType: string, initHeight: number, borderId: string)=>{
        if (!editor) return;

        const conversionId = getLocalConversionId(borderId);
        const shapes = editor.getCurrentPageShapes();
        const tagShapes = shapes.filter(
            react => react.meta.conversionId === conversionId &&
                react.type === reactType &&
                react.meta.toolName === toolName
        );

        tagShapes.forEach(shape=> {
            editor.updateShape({
                id: shape!.id, // required
                type: shape!.type, // required
                x: shape!.x -  (childFrameWidth + childFrameGap) / 2,
            })
        })


        let tagShape = tagShapes.pop();

        const center = editor.getViewportPageBounds().center;

        let lowestY = 0;
        let leftX = Infinity;  // 初始化为最大值
        let rightX = -Infinity;  // 初始化为最小值

        if (!tagShape) {
            shapes.forEach((shape) => {
                if (shape) {
                    const frameShape = shape as TLFrameShape;
                    if (frameShape.y && frameShape.props.h) {
                        lowestY = Math.max(lowestY, frameShape.y + frameShape.props.h);
                    }
                    if (frameShape.x) {
                        leftX = Math.min(leftX, frameShape.x);
                    }
                    if (frameShape.x && frameShape.props.w) {
                        rightX = Math.max(rightX, frameShape.x + frameShape.props.w);
                    }
                }
            });
        }

        // 如果没有找到任何 shape，使用默认值
        if (leftX === Infinity) leftX = 0;
        if (rightX === -Infinity) rightX = 0;

        const centerX = (leftX + rightX) / 2;

        let startY =  centerX ? lowestY + 50 : center.y - 150 / 2;
        let startX = centerX ? centerX - 500 / 2 : center.x - 500 / 2;

        if (tagShape) {
            startY = tagShape.y;
            startX = tagShape.x + (childFrameWidth + childFrameGap) / 2
        }

        runWithoutHistory(() => {
            editor.createShape({
                type: reactType,
                x: startX,
                y: startY,
                props: {
                    index:1,
                    text: note,
                    status: t('waiting_confirm'),
                    w: 500,
                    h: initHeight,
                    loading: true,
                    taskId
                },
                meta: {
                    taskId,
                    conversionId,
                    toolName
                }
            });
        })


    }, [editor])

    const createThumbnailShape = useCallback((taskDetail: taskProcess, borderId: string)=>{
        if (!editor) return;
        const imageData = taskDetail.response as FetchImagesTaskProcess;
        const taskId = taskDetail.id || 0;

        const conversionId = getLocalConversionId(borderId);

        const shapes = editor.getCurrentPageShapes();
        const tagShapes = shapes.filter(react => react.meta.conversionId === conversionId && react.type === 'thumbnail-image-shape');

        tagShapes.forEach(shape=> {
            editor.updateShape({
                id: shape!.id, // required
                type: shape!.type, // required
                x: shape!.x -  (childFrameWidth + childFrameGap) / 2,
            })
        })

        let tagShape = tagShapes.pop();

        const center = editor.getViewportPageBounds().center;

        let lowestY = 0;
        let leftX = Infinity;  // 初始化为最大值
        let rightX = -Infinity;  // 初始化为最小值

        if (!tagShape) {
            shapes.forEach((shape) => {
                if (shape) {
                    const frameShape = shape as TLFrameShape;
                    if (frameShape.y && frameShape.props.h) {
                        lowestY = Math.max(lowestY, frameShape.y + frameShape.props.h);
                    }
                    if (frameShape.x) {
                        leftX = Math.min(leftX, frameShape.x);
                    }
                    if (frameShape.x && frameShape.props.w) {
                        rightX = Math.max(rightX, frameShape.x + frameShape.props.w);
                    }
                }
            });
        }

        // 如果没有找到任何 frame，使用默认值
        if (leftX === Infinity) leftX = 0;
        if (rightX === -Infinity) rightX = 0;

        const centerX = (leftX + rightX) / 2;

        let startY =  centerX ? lowestY + 50 : center.y - 150 / 2;
        let startX = centerX ? centerX - 500 / 2 : center.x - 500 / 2;

        if (tagShape) {
            startY = tagShape.y;
            startX = tagShape.x + (childFrameWidth + childFrameGap) / 2
        }


        runWithoutHistory(() => {
            editor.createShape({
                type: 'thumbnail-image-shape',
                x: startX,
                y: startY,
                props: {
                    index:1,
                    text: imageData.note,
                    status: t('waiting_confirm'),
                    w: 500,
                    h: 320,
                    imageData: taskDetail,
                    loading: true,
                    taskId
                },
                meta: {
                    taskId,
                    conversionId
                }
            });
        })

    }, [editor])

    const createTagsShape = useCallback((taskDetail: taskProcess, boardId: string)=>{
        if (!editor) return;
        const tagsData = taskDetail.response as GenerateTagsTaskProcess;
        const taskId = taskDetail.id || 0;
        const conversionId = getLocalConversionId(boardId);

        const shapes = editor.getCurrentPageShapes();
        const tagShapes = shapes.filter(react => react.meta.conversionId === conversionId && react.type === 'tags-shape');

        tagShapes.forEach(shape=> {
            editor.updateShape({
                id: shape!.id, // required
                type: shape!.type, // required
                x: shape!.x -  (childFrameWidth + childFrameGap) / 2,
            })
        })

        let tagShape = tagShapes.pop();

        const center = editor.getViewportPageBounds().center;

        let lowestY = 0;
        let leftX = Infinity;  // 初始化为最大值
        let rightX = -Infinity;  // 初始化为最小值

        if (!tagShape) {
            shapes.forEach((shape) => {
                if (shape) {
                    const frameShape = shape as TLFrameShape;
                    if (frameShape.y && frameShape.props.h) {
                        lowestY = Math.max(lowestY, frameShape.y + frameShape.props.h);
                    }
                    if (frameShape.x) {
                        leftX = Math.min(leftX, frameShape.x);
                    }
                    if (frameShape.x && frameShape.props.w) {
                        rightX = Math.max(rightX, frameShape.x + frameShape.props.w);
                    }
                }
            });
        }

        // 如果没有找到任何 frame，使用默认值
        if (leftX === Infinity) leftX = 0;
        if (rightX === -Infinity) rightX = 0;

        const centerX = (leftX + rightX) / 2;

        let startY =  centerX ? lowestY + 50 : center.y - 150 / 2;
        let startX = centerX ? centerX - 500 / 2 : center.x - 500 / 2;

        if (tagShape) {
            startY = tagShape.y;
            startX = tagShape.x + (childFrameWidth + childFrameGap) / 2
        }

        runWithoutHistory(() => {
            editor.createShape({
                type: 'tags-shape',
                x: startX,
                y: startY,
                props: {
                    index:1,
                    text: tagsData.note,
                    status: t('waiting_confirm'),
                    w: 500,
                    h: 150,
                    imageData: taskDetail,
                    loading: true,
                    taskId
                },
                meta: {
                    taskId,
                    conversionId
                }
            });
        })

    }, [editor])

    const replaceTagsShape = useCallback((taskDetail: taskProcess, taskId: number)=>{
        if (!editor) return;
        const shapes = editor.getCurrentPageShapes();
        const mainShape = shapes.find(shape => shape.type === 'tags-shape' && shape.meta.taskId === taskId);

        if (!mainShape) return;

        runWithoutHistory(()=>{
            editor.updateShapes([
                {
                    id: mainShape.id, // required
                    type: mainShape.type, // required
                    props: {
                        imageData: taskDetail,
                        loading: false,
                    }
                },
            ])
        })

    }, [editor])


    const replaceThumbnailShape = useCallback((taskDetail: taskProcess, taskId: number)=>{
        if (!editor) return;
        const shapes = editor.getCurrentPageShapes();
        const mainShape = shapes.find(shape => shape.type === 'thumbnail-image-shape' && shape.meta.taskId === taskId);

        const imageData = taskDetail.response as FetchImagesTaskProcess;

        const centerImageCount = imageData.data.filter(item=>item.isCenter).length;

        const rowNum =  Math.ceil(centerImageCount / 3)

        const containerHeight = (rowNum * 150) + ( 10 * (rowNum + 1) );

        if (!mainShape) return;

        runWithoutHistory(()=>{
            editor.updateShapes([
                {
                    id: mainShape.id, // required
                    type: mainShape.type, // required
                    props: {
                        imageData: taskDetail,
                        loading: false,
                        h: containerHeight + 40,
                    }
                },
            ])
        })

    }, [editor])
    return {
        stepsProcess,
        setLabelToFrame,
        replaceLabelsFrame,
        replaceImagesFrame,
        updateMainContainerStatus,
        createHoverBar,
        syncCanvasData,
        createThumbnailShape,
        createTagsShape,
        replaceTagsShape,
        replaceThumbnailShape,
        createCustomShape,
        setLabelToFrameNotDependent,
        replaceDialogFrame,
        replaceDescribeFrame,
        doubleClickArouse,
        stepsContainer,
        fitFrameContainer,
        createDialogHoverBar,
        getSelectFrameShape,
        selectFrameShape,
        updateParentShape,
        redrawYAxis,
        selectShape,
        getLastStepId
    };
};
